import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { Helmet } from 'react-helmet'

import CardList from 'components/common/CardList'
import Content from 'components/common/Content'
import { ServicesCard } from 'components/dashboard/cards/ServicesCard'
import DashboardUpgradeBanner from 'components/dashboard/footer/DashboardUpgradeBanner'
import TutorialBanner from 'components/dashboard/footer/TutorialBanner'
import Header, { Heading } from 'components/navigation/Header'
import useAttackResource from 'hooks/use-attack-resource'
import useAuth from 'hooks/use-auth'
import useClientResource from 'hooks/use-client-resource'
import theme from 'theme'

import AgentsCard from './cards/AgentsCard'
import RecentExperimentsCard from './cards/RecentExperimentsCard'
import RecentScenariosCard from './cards/RecentScenariosCard'
import ScheduledExperimentsCard from './cards/ScheduledExperimentsCard'
import TotalAttacksRunCard from './cards/TotalAttacksRunCard'
import UniqueTargetsAttackedCard from './cards/UniqueTargetsAttackedCard'
import DashboardHeader from './header/DashboardHeader'
import HeaderSlider from './header/HeaderSlider'

export const CARD_WIDTH = 500

const Placeholder = ({ children }) => (
  <div
    css={{
      width: '100%',
      height: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.gray[0],
      marginTop: theme.space[1]
    }}
  >
    {children}
  </div>
)

Placeholder.propTypes = {
  children: PropTypes.node
}

export default function Dashboard() {
  const { isFreeEdition, hasReliabilityManagement } = useAuth()

  const {
    activeScenarios,
    activeScenariosLoading,
    completed: completedAttacks,
    active: activeAttacks,
    partialCompletedAttacksLoaded,
    kubernetesAttacksLoading,
    applicationAttacksLoading
  } = useAttackResource({ fetchCompletedFully: false })

  // using partial fetch only as we don't need the full fetch anymore
  const attacksLoading =
    !partialCompletedAttacksLoaded ||
    kubernetesAttacksLoading ||
    applicationAttacksLoading

  const allAttacks = [].concat(activeAttacks, completedAttacks)
  const clientResource = useClientResource()

  return (
    <Fragment>
      <Helmet title="Dashboard" />
      <Header>
        <Heading>Dashboard</Heading>
      </Header>
      <Content data-test-dashboard id="Dashboard">
        <DashboardHeader />
        <CardList
          css={{
            marginTop: theme.space[4]
          }}
          minWidth={CARD_WIDTH}
        >
          {hasReliabilityManagement && <ServicesCard />}
          <AgentsCard
            allAttacks={allAttacks}
            attacksLoading={attacksLoading}
            clientResource={clientResource}
          />
          <TotalAttacksRunCard />
          <RecentScenariosCard
            scenarios={activeScenarios}
            scenariosLoading={activeScenariosLoading}
          />
          <RecentExperimentsCard
            allAttacks={allAttacks}
            attacksLoading={attacksLoading}
          />
          <ScheduledExperimentsCard />
          {!isFreeEdition && <UniqueTargetsAttackedCard />}
        </CardList>
        {!isFreeEdition && <TutorialBanner />}
        {isFreeEdition && (
          <div css={{ marginTop: theme.space[3] }}>
            <HeaderSlider />
          </div>
        )}
        {isFreeEdition && <DashboardUpgradeBanner />}
      </Content>
    </Fragment>
  )
}
